if (!customElements.get('process-steps')) {
  customElements.define(
    'process-steps',
    class ProcessSteps extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.process-steps__item'));
        this.items.forEach((item) => {
          item.querySelector('.process-steps__row').addEventListener('click', () => this.toggle(item));
        });
        this.syncVideos();
        this.setUpPreview();
      }

      // With a mouse, the photo of the step under the cursor floats beside it and glides after it. It also follows
      // when the page scrolls a different step under a still cursor. The open step already shows its photo, so it gets none.
      setUpPreview() {
        this.preview = this.querySelector('.process-steps__preview');
        if (!this.preview || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        this.previews = Array.from(this.preview.children);
        this.pointer = null;

        this.addEventListener('pointermove', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.pointer = { x: event.clientX, y: event.clientY };
          this.updatePreview();
        });
        this.addEventListener('pointerleave', () => {
          this.pointer = null;
          this.updatePreview();
        });
        window.addEventListener('scroll', () => this.pointer && this.updatePreview(), { passive: true });
      }

      updatePreview() {
        const under = this.pointer && document.elementFromPoint(this.pointer.x, this.pointer.y);
        const item = under && under.closest('.process-steps__item');
        const index = item && !item.classList.contains('is-open') ? this.items.indexOf(item) : -1;
        const media = this.previews[index];
        const show = Boolean(media && media.querySelector('img'));

        this.preview.classList.toggle('is-visible', show);
        // Once hidden, it reappears at the cursor rather than gliding over from where it was last.
        if (!show) {
          this.placed = false;
          return;
        }
        this.previews.forEach((m) => m.classList.toggle('is-current', m === media));
        if (!this.frame) this.frame = requestAnimationFrame(() => this.movePreview());
      }

      // Eases toward a spot just right of the cursor, vertically centred on it, kept inside the window.
      movePreview() {
        this.frame = null;
        if (!this.pointer) return;
        const width = this.preview.offsetWidth;
        const height = this.preview.offsetHeight;
        const target = {
          x: Math.min(this.pointer.x + 24, window.innerWidth - width - 16),
          y: Math.min(Math.max(this.pointer.y - height / 2, 16), window.innerHeight - height - 16),
        };
        if (!this.placed) {
          this.position = target;
          this.placed = true;
        }
        this.position.x += (target.x - this.position.x) * 0.18;
        this.position.y += (target.y - this.position.y) * 0.18;
        this.preview.style.transform = `translate3d(${this.position.x}px, ${this.position.y}px, 0)`;
        if (Math.abs(target.x - this.position.x) + Math.abs(target.y - this.position.y) > 0.5) {
          this.frame = requestAnimationFrame(() => this.movePreview());
        }
      }

      // Only the open step's video plays (silently, on a loop); every other video is paused and rewound.
      syncVideos() {
        this.items.forEach((item) => {
          const video = item.querySelector('video');
          if (!video) return;
          if (item.classList.contains('is-open')) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      }

      // Opens the clicked step and closes the others; clicking the open step closes it.
      toggle(clicked) {
        const opening = !clicked.classList.contains('is-open');
        const row = clicked.querySelector('.process-steps__row');
        const startTop = row.getBoundingClientRect().top;

        this.items.forEach((item) => {
          const open = item === clicked && opening;
          item.classList.toggle('is-open', open);
          item.querySelector('.process-steps__row').setAttribute('aria-expanded', open);
        });

        this.syncVideos();
        if (this.previews) this.updatePreview();

        // While the step above closes, keep the clicked row where it was on screen so the page doesn't jump.
        cancelAnimationFrame(this.holdFrame);
        const until = performance.now() + 800;
        const hold = () => {
          const drift = row.getBoundingClientRect().top - startTop;
          if (Math.abs(drift) > 0.5) window.scrollBy(0, drift);
          if (performance.now() < until) this.holdFrame = requestAnimationFrame(hold);
        };
        this.holdFrame = requestAnimationFrame(hold);
      }
    }
  );
}
